import LabelIcon from '@mui/icons-material/LabelOutlined';
import { Autocomplete, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Stack, TextField, Typography } from '@mui/material';
import React from 'react';
import { Bookmark } from '../../../functions/src/types';
import { useDataContext } from '../context/DataContext';
import { useUIContext } from '../context/UIContext';

interface BulkTagDialogProps {
  open: boolean;
  selectedBookmarks: Bookmark[];
  onClose: () => void;
  onSaved: () => void;
}

const normalizeTags = (tags: string[]) =>
  Array.from(new Set(tags.map(tag => tag.trim()).filter(tag => tag.length > 0)));

export const BulkTagDialog: React.FC<BulkTagDialogProps> = ({
  open,
  selectedBookmarks,
  onClose,
  onSaved,
}) => {
  const { allTags, saveBulkModifiedBookmarks } = useDataContext();
  const { saving } = useUIContext();
  const [addTags, setAddTags] = React.useState<string[]>([]);
  const [removeTags, setRemoveTags] = React.useState<string[]>([]);

  React.useEffect(() => {
    if (open) {
      setAddTags([]);
      setRemoveTags([]);
    }
  }, [open]);

  const selectedBookmarkIds = selectedBookmarks
    .map(bookmark => bookmark.fid)
    .filter((bookmarkId): bookmarkId is string => Boolean(bookmarkId));

  const removableTags = React.useMemo(() => {
    const tagSet = new Set<string>();
    selectedBookmarks.forEach(bookmark => {
      (bookmark.tags || []).forEach(tag => tagSet.add(tag));
    });
    return Array.from(tagSet).sort((a, b) => a.localeCompare(b));
  }, [selectedBookmarks]);

  const handleSave = async () => {
    const normalizedAddTags = normalizeTags(addTags);
    const normalizedRemoveTags = normalizeTags(removeTags);
    const succeeded = await saveBulkModifiedBookmarks(selectedBookmarkIds, normalizedAddTags, normalizedRemoveTags);
    if (succeeded) {
      onSaved();
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <Stack spacing={2}>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <LabelIcon color="secondary" />
            Edit tags for {selectedBookmarks.length} bookmark{selectedBookmarks.length === 1 ? '' : 's'}
          </Box>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Add tags to every selected bookmark, and remove any selected tags wherever they currently exist.
            </Typography>
            <Autocomplete
              multiple
              freeSolo
              autoSelect
              options={allTags.filter(tag => !addTags.includes(tag))}
              value={addTags}
              onChange={(_event, newValue) => setAddTags(normalizeTags(newValue as string[]))}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    {...getTagProps({ index })}
                    key={option}
                    label={option}
                    size="small"
                    color="secondary"
                  />
                ))
              }
              renderInput={(params) => (
                <TextField {...params} label="Add tags" variant="outlined" size="small" fullWidth />
              )}
            />
            <Autocomplete
              multiple
              options={removableTags.filter(tag => !removeTags.includes(tag))}
              value={removeTags}
              onChange={(_event, newValue) => setRemoveTags(normalizeTags(newValue as string[]))}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    {...getTagProps({ index })}
                    key={option}
                    label={option}
                    size="small"
                    color="secondary"
                  />
                ))
              }
              renderInput={(params) => (
                <TextField {...params} label="Remove tags" variant="outlined" size="small" fullWidth />
              )}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} variant="outlined">Cancel</Button>
          <Button
            onClick={handleSave}
            variant="contained"
            color="secondary"
            disabled={saving || selectedBookmarkIds.length === 0 || (addTags.length === 0 && removeTags.length === 0)}
          >
            {saving ? 'Applying...' : 'Apply'}
          </Button>
        </DialogActions>
      </Stack>
    </Dialog>
  );
};
